import { Injectable } from '@angular/core';
import {
  ApiResponseTemplate,
  LogsQueryParams,
  OrgTemplate,
  TemplateLogsApiResponsePaginated,
  TemplateLookupResponse,
  TemplateModel,
  TemplatePayload,
  TemplateResponse,
  UpdateTemplateQuestionSequencePayload,
} from './bip-template.interface';
import { BipTemplateSavePayload } from './bip-template-form/bip-template-form.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import { ApiResponse } from '../../core/models/api-response.models';
import { ManageSettingsService } from '../manage-settings/manage-settings.service';
import { AuditFieldChange } from '../audit-logs/audit-logs.interface';

@Injectable({
  providedIn: 'root',
})
export class BipTemplateService {
  private readonly API_URL = `${environment.apiUrl}`;
  private readonly templateLogsSubject =
    new BehaviorSubject<TemplateLogsApiResponsePaginated | null>(null);
  templateLogs$ = this.templateLogsSubject.asObservable();
  private readonly bipTemplateSubject = new BehaviorSubject<ApiResponse<
    OrgTemplate[]
  > | null>(null);
  bipTemplate$ = this.bipTemplateSubject.asObservable();
  constructor(
    private readonly errorHandler: ErrorHandlingService,
    private readonly http: HttpClient,
    private readonly managSettingService: ManageSettingsService
  ) {}

  private buildTemplateLogsQueryParams(
    queryParams: LogsQueryParams
  ): HttpParams {
    let params = new HttpParams()
      .set('Page', queryParams.page.toString())
      .set('PageSize', queryParams.pageSize.toString());

    if (queryParams.sortOrder) {
      params = params.set('sortOrder', queryParams.sortOrder);
    }

    if (queryParams.sort) {
      params = params.set('sort', queryParams.sort);
    }
    if (queryParams.templateId) {
      params = params.set('templateId', queryParams.templateId);
    }

    if (queryParams.userId) {
      params = params.set('userId', queryParams.userId.toString());
    }
    if (queryParams.title) {
      params = params.set('title', queryParams.title.toString());
    }
    if (queryParams.dataAuditTypeId) {
      params = params.set(
        'dataAuditTypeId',
        queryParams.dataAuditTypeId.toString()
      );
    }
    if (queryParams.startDate) {
      params = params.set('startDate', queryParams.startDate.toString());
    }
    if (queryParams.endDate) {
      params = params.set('endDate', queryParams.endDate.toString());
    }
    if (queryParams.organizationId) {
      params = params.set(
        'organizationId',
        queryParams.organizationId.toString()
      );
    }
    return params;
  }

  getTemplateLogs(
    queryParams: LogsQueryParams
  ): Observable<TemplateLogsApiResponsePaginated> {
    return this.fetchTemplateLogs(queryParams).pipe(
      tap(response => this.templateLogsSubject.next(response))
    );
  }

  getTemplateLogsForExport(
    queryParams: LogsQueryParams
  ): Observable<TemplateLogsApiResponsePaginated> {
    return this.fetchTemplateLogs(queryParams);
  }

  private fetchTemplateLogs(
    queryParams: LogsQueryParams
  ): Observable<TemplateLogsApiResponsePaginated> {
    const params = this.buildTemplateLogsQueryParams(queryParams);
    return this.http
      .get<TemplateLogsApiResponsePaginated>(
        `${this.API_URL}/BipTemplateLog/list`,
        {
          params,
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getTemplateLogsLookup(organizationId: number) {
    return this.http
      .get<
        ApiResponse<TemplateLookupResponse>
      >(`${this.API_URL}/BipTemplateLog/get-template-lookups?organizationId=${organizationId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBipTemplateByOrgId(orgId: number) {
    return this.http
      .get<
        ApiResponse<OrgTemplate[]>
      >(`${this.API_URL}/BipTemplate/get-by-org-id?orgId=${orgId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  setDefaultTemplate(orgId: number, templateId: number) {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/BipTemplate/set-default-template?organizationId=${orgId}&templateId=${templateId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateTemplateStatus(
    templateId: number,
    isActive: boolean
  ): Observable<ApiResponse<void>> {
    const params = new HttpParams()
      .set('templateId', templateId.toString())
      .set('isActive', isActive.toString());

    return this.http
      .put<
        ApiResponse<void>
      >(`${this.API_URL}/BipTemplate/update-status`, {}, { params })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteBipTemplate(templateId: number) {
    return this.http
      .delete<
        ApiResponse<void>
      >(`${this.API_URL}/BipTemplate/${templateId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getTemplateById(id: number) {
    return this.http
      .get<ApiResponse<TemplateModel>>(`${this.API_URL}/BipTemplate/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  editTemplate(
    payload: TemplatePayload | BipTemplateSavePayload,
    id: number
  ): Observable<ApiResponse<TemplateResponse>> {
    return this.http
      .put<
        ApiResponse<TemplateResponse>
      >(`${this.API_URL}/BipTemplate/update?templateId=${id}`, payload)
      .pipe(
        tap(response => {
          this.getBipTemplateListByOrgId(
            payload.organizationId,
            true
          ).subscribe();
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createTemplate(
    payload: TemplatePayload | BipTemplateSavePayload
  ): Observable<ApiResponse<TemplateResponse>> {
    return this.http
      .post<
        ApiResponse<TemplateResponse>
      >(`${this.API_URL}/BipTemplate/create`, payload)
      .pipe(
        tap(response => {
          this.getBipTemplateListByOrgId(
            payload.organizationId,
            true
          ).subscribe();
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  getBipTemplateListByOrgId(orgId: number, isActive?: boolean) {
    let params = new HttpParams().set('orgId', orgId.toString());
    if (typeof isActive === 'boolean') {
      params = params.set('isActive', isActive.toString());
    }

    return this.http
      .get<
        ApiResponseTemplate<OrgTemplate[]>
      >(`${this.API_URL}/BipTemplate/get-by-org-id`, { params })
      .pipe(
        tap(response => this.bipTemplateSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateTemplateQuestionSequence(
    templateId: number,
    questionIds: number[],
    _modifiedFields: AuditFieldChange[]
  ) {
    const payload: UpdateTemplateQuestionSequencePayload = {
      bipTemplateId: templateId,
      questionSequenceNumbers: questionIds.map((questionId, index) => ({
        questionId,
        sequenceNumber: index + 1,
      })),
      _modifiedFields,
    };
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.API_URL}/biptemplate/update-template-question-sequence?templateId=${templateId}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
